import { styled } from "goober";
import {
  type ClipboardEvent,
  type KeyboardEvent,
  useRef,
  useState,
} from "react";
import { AttachmentLabel, AttachmentPreview } from "../../AttachmentPreviews";
import { Box } from "../../Box";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import { DEFAULT_OIDC_SCOPES, parseScopes } from "./constants";
import { Field, FieldActions, FieldLink, HelperRow, KeptValue } from "./shared";
import { fieldOutline } from "../../fieldOutline";

// Sign-in is an OIDC request only when openid is among the scopes.
const REQUIRED_SCOPE = "openid";

const ChipBox = styled("div")`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 4px 6px;
  background-color: var(--surface-primary);
  ${fieldOutline("&:focus-within")}
  border-radius: 8px;
  cursor: text;
`;

const ChipInput = styled("input")`
  flex: 1;
  min-width: 96px;
  height: 24px;
  padding: 0 8px;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text-primary);
  font-family: InterVariable;
  font-size: 12px;
  &::placeholder {
    opacity: 0.3;
  }
`;

function ScopeEditor({
  scopes,
  onChange,
}: {
  scopes: string[];
  onChange: (scopes: string[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");

  function add(text: string) {
    const added = parseScopes(text).filter((s) => !scopes.includes(s));
    if (added.length) onChange([...scopes, ...added]);
    setDraft("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === " " || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft) {
      const last = scopes.at(-1);
      if (last && last !== REQUIRED_SCOPE) onChange(scopes.slice(0, -1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    add(draft + e.clipboardData.getData("text"));
  }

  return (
    <ChipBox onClick={() => inputRef.current?.focus()}>
      {scopes.map((scope) =>
        scope === REQUIRED_SCOPE ? (
          <Tooltip key={scope} content="Required for OIDC sign-in">
            <AttachmentLabel
              as="span"
              style={{ cursor: "default", background: "var(--alpha4)" }}
            >
              <Icon name="lock" size={10} />
              <Text variant="text-small-md">{scope}</Text>
            </AttachmentLabel>
          </Tooltip>
        ) : (
          <AttachmentPreview
            key={scope}
            aria-label={`Remove ${scope}`}
            onPress={() => onChange(scopes.filter((s) => s !== scope))}
          >
            <span className="file-icon">
              <Icon name="key" size={10} />
            </span>
            <span className="remove-icon">
              <Icon name="x" size={10} />
            </span>
            <Text variant="text-small-md" ellipsis>
              {scope}
            </Text>
          </AttachmentPreview>
        )
      )}
      <ChipInput
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => add(draft)}
        placeholder="Add a scope"
        aria-label="Add a scope"
      />
    </ChipBox>
  );
}

export function ScopesField({
  value,
  onChange,
  existing,
}: {
  value: string;
  onChange: (value: string) => void;
  existing?: boolean;
}) {
  const scopes = parseScopes(value);
  const keepingCurrent = existing && scopes.length === 0;
  const isDefault = scopes.join(" ") === DEFAULT_OIDC_SCOPES.join(" ");
  const setScopes = (next: string[]) =>
    onChange(
      [REQUIRED_SCOPE, ...next.filter((s) => s !== REQUIRED_SCOPE)].join(" ")
    );

  return (
    <Field>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">Scopes</Text>
      </Box>
      {keepingCurrent ? (
        <KeptValue
          label="Current scopes are kept"
          onReplace={() => onChange(DEFAULT_OIDC_SCOPES.join(" "))}
        />
      ) : (
        <ScopeEditor scopes={scopes} onChange={setScopes} />
      )}
      <HelperRow>
        <Text variant="text-small" color="var(--text-secondary)">
          {keepingCurrent
            ? "Replacing them starts from the defaults."
            : "Requested from your IdP at sign-in. Press Enter or space to add one."}
        </Text>
        {existing && !keepingCurrent ? (
          <FieldActions>
            <FieldLink onPress={() => onChange("")}>
              <Text variant="text-small">Keep current scopes</Text>
            </FieldLink>
          </FieldActions>
        ) : (
          !existing &&
          !isDefault && (
            <FieldActions>
              <FieldLink
                onPress={() => onChange(DEFAULT_OIDC_SCOPES.join(" "))}
              >
                <Text variant="text-small">Reset to defaults</Text>
              </FieldLink>
            </FieldActions>
          )
        )}
      </HelperRow>
    </Field>
  );
}
